import { useSearchParams } from "react-router-dom";
import { getLocalStorageValue, setLocalStorageValue } from "utils/storage";
import { TaskBase, TaskBaseFields, TaskBaseTypes } from "pages/project/common/taskBase.typing";
import { LIST_URL_PARAM_NAMES, ListFilterParams, ListViewType } from "components/typings";

type UseTaskBaseFilterType = [ListFilterParams<TaskBase>, (params: ListFilterParams<TaskBase>) => void];

const DEFAULT_VIEW_VALUE: ListViewType = "card";
const DEFAULT_GROUPED_VALUE: boolean = false;

export const useTaskBaseFilter = (objectType: TaskBaseTypes): UseTaskBaseFilterType => {
  const [searchParams, setSearchParameter] = useSearchParams();

  const sortBy = searchParams.get(LIST_URL_PARAM_NAMES.SORT_BY) as TaskBaseFields;
  const sortDirection = (searchParams.get(LIST_URL_PARAM_NAMES.SORT_DIR) as "asc" | "desc") ?? "asc";
  const term = searchParams.get(LIST_URL_PARAM_NAMES.TERM);
  const view = getFromUrlOrLocalStorage(objectType, searchParams.get(LIST_URL_PARAM_NAMES.VIEW));
  const isGrouped = getGroupedFromUrlOrLocalStorage(objectType, searchParams.get(LIST_URL_PARAM_NAMES.GROUPED));

  const updateSearchParams = (fromParams: ListFilterParams<TaskBase>) => {
    const params = new URLSearchParams();
    if (fromParams.sortBy) {
      params.set(LIST_URL_PARAM_NAMES.SORT_BY, fromParams.sortBy);
      params.set(LIST_URL_PARAM_NAMES.SORT_DIR, fromParams.sortDirection);
    }
    if (fromParams.term) {
      params.set(LIST_URL_PARAM_NAMES.TERM, fromParams.term);
    }
    if (fromParams.view) {
      saveToLocalStorage(objectType, fromParams.view);
      params.set(LIST_URL_PARAM_NAMES.VIEW, fromParams.view);
    } else {
      const view = readFromLocalStorage(objectType) ?? DEFAULT_VIEW_VALUE;
      params.set(LIST_URL_PARAM_NAMES.VIEW, view);
    }
    if (fromParams.isGrouped !== undefined) {
      saveGroupedToLocalStorage(objectType, fromParams.isGrouped);
      params.set(LIST_URL_PARAM_NAMES.GROUPED, fromParams.isGrouped.toString());
    } else {
      const isGrouped = readGroupedFromLocalStorage(objectType) ?? DEFAULT_GROUPED_VALUE;
      params.set(LIST_URL_PARAM_NAMES.GROUPED, isGrouped.toString());
    }
    setSearchParameter(params);
  };

  return [{ sortBy, term, view, sortDirection, isGrouped }, updateSearchParams];
};

const getFromUrlOrLocalStorage = (objectType: TaskBaseTypes, fromSearchParam: string | null): ListViewType => {
  if (fromSearchParam) {
    return fromSearchParam as ListViewType;
  }

  const fromLocalStorage = readFromLocalStorage(objectType);
  if (fromLocalStorage) {
    return fromLocalStorage;
  }

  return DEFAULT_VIEW_VALUE;
};

const readFromLocalStorage = (objectType: TaskBaseTypes) => {
  return getLocalStorageValue<ListViewType>(`pp-view-${objectType}`, `read view param for ${objectType}`);
};

const saveToLocalStorage = (objectType: TaskBaseTypes, view: ListViewType) => {
  setLocalStorageValue(`pp-view-${objectType}`, view, `save view param for ${objectType}`);
};

const getGroupedFromUrlOrLocalStorage = (objectType: TaskBaseTypes, fromSearchParam: string | null): boolean => {
  if (fromSearchParam !== null) {
    return fromSearchParam === "true";
  }

  const fromLocalStorage = readGroupedFromLocalStorage(objectType);
  if (fromLocalStorage !== null) {
    return fromLocalStorage;
  }

  return false;
};

const readGroupedFromLocalStorage = (objectType: TaskBaseTypes) => {
  return getLocalStorageValue<boolean>(`pp-grouped-${objectType}`, `read grouped param for ${objectType}`);
};

const saveGroupedToLocalStorage = (objectType: TaskBaseTypes, isGrouped: boolean) => {
  setLocalStorageValue(`pp-grouped-${objectType}`, isGrouped, `save grouped param for ${objectType}`);
};
